/**
 * General string utility functions.
 * These functions are designed to be reusable across different projects.
 */

/**
 * Converts a string to sentence case (first letter uppercase, rest lowercase).
 * @param {string} str - String to convert
 * @returns {string} String in sentence case
 * @example
 * toSentenceCase('HELLO WORLD') // 'Hello world'
 * toSentenceCase('hello world') // 'Hello world'
 */
export function toSentenceCase(str) {
    if (!str || str.length === 0) {
        return str;
    }
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Converts a string to title case (first letter of each word uppercase, rest lowercase).
 * @param {string} str - String to convert
 * @returns {string} String in title case
 * @example
 * toTitleCase('JANE AUSTEN') // 'Jane Austen'
 * toTitleCase('jane austen') // 'Jane Austen'
 * toTitleCase('jAnE aUsTeN') // 'Jane Austen'
 */
export function toTitleCase(str) {
    if (!str || str.length === 0) {
        return str;
    }
    return str.split(' ').map(toSentenceCase).join(' ');
}

/**
 * Truncates text to a maximum length, appending an ellipsis when it overflows so the
 * result (ellipsis included) never exceeds maxLength. Counts by Unicode code points,
 * so it never splits a surrogate pair (e.g. emoji) and matches a backend character
 * count (mb_strlen). Text within the limit is returned untouched.
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum allowed length, in code points, of the result
 * @param {string} [ellipsis='…'] - Indicator appended when the text is truncated
 * @returns {string} The original text, or it truncated with a trailing ellipsis
 * @example
 * truncateWithEllipsis('hello world', 8) // 'hello w…'
 * truncateWithEllipsis('short', 8)       // 'short'
 */
export function truncateWithEllipsis(text, maxLength, ellipsis = '…') {
    const safeText = text || '';
    const characters = Array.from(safeText);
    if (characters.length <= maxLength) {
        return safeText;
    }
    return (
        characters
            .slice(0, maxLength - Array.from(ellipsis).length)
            .join('')
            .trimEnd() + ellipsis
    );
}
